import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../components/ui/select';
import { Lock, User, ChevronRight, Code, Shield, Users, Sparkles, ArrowLeft, Monitor, BookOpen, Award } from 'lucide-react';

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [selectedRole, setSelectedRole] = useState('student');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const user = await login(email.trim(), password, selectedRole);
      const role = user.role;
      const dashboardMap = { admin: '/admin/dashboard', teacher: '/teacher/dashboard', student: '/student/dashboard', parent: '/parent/dashboard' };
      navigate(dashboardMap[role] || '/');
      toast.success(`Welcome, ${user.name}!`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const roleLabels = { student: 'Student Portal', teacher: 'Teacher Portal', admin: 'Admin Console', parent: 'Parent Hub' };
  const roleColors = { student: 'from-blue-600 to-cyan-500', teacher: 'from-violet-600 to-purple-500', admin: 'from-slate-700 to-slate-900', parent: 'from-rose-500 to-amber-500' };

  return (
    <div className="min-h-screen flex" style={{ fontFamily: "'Nunito', sans-serif" }} data-testid="login-page">

      {/* ═══════ LEFT PANEL — Branding & Trust ═══════ */}
      <div className="hidden lg:flex lg:w-[52%] relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #1E3A8A 0%, #312E81 50%, #1E40AF 100%)' }}>
        {/* Decorative pattern */}
        <div className="absolute inset-0 opacity-[0.04]" style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%23ffffff\' fill-opacity=\'1\'%3E%3Cpath d=\'M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")'}} />
        {/* Floating code snippets (decorative) */}
        <div className="absolute top-20 right-12 px-4 py-2.5 rounded-xl bg-white/[0.07] backdrop-blur-sm border border-white/10 font-mono text-[11px] text-cyan-300/60 transform rotate-3">
          <span className="text-purple-300/50">def</span> learn_coding():<br/>
          &nbsp;&nbsp;<span className="text-cyan-300/50">return</span> "future_ready"
        </div>
        <div className="absolute bottom-32 left-10 px-4 py-2.5 rounded-xl bg-white/[0.07] backdrop-blur-sm border border-white/10 font-mono text-[11px] text-emerald-300/60 transform -rotate-2">
          &lt;<span className="text-amber-300/50">Student</span> growth=<span className="text-emerald-300/50">"unlimited"</span> /&gt;
        </div>
        <div className="absolute top-[45%] right-24 px-4 py-2.5 rounded-xl bg-white/[0.07] backdrop-blur-sm border border-white/10 font-mono text-[11px] text-amber-300/60 transform rotate-1">
          while(curious) &#123; create(); &#125;
        </div>

        {/* Content */}
        <div className="relative z-10 flex flex-col justify-between p-12 xl:p-16 w-full">
          {/* Logo & tagline */}
          <div>
            <div className="flex items-center gap-3 mb-2 cursor-pointer" onClick={() => navigate('/')}>
              <img src="/assets/logo.png" alt="CodeITT" className="h-14 w-auto object-contain rounded-xl" />
              <div>
                <h1 className="text-2xl font-extrabold text-white tracking-tight">CodeITT <span className="text-cyan-300">Academy</span></h1>
                <p className="text-[11px] font-bold text-blue-200/60 uppercase tracking-[0.2em]">Learning Platform</p>
              </div>
            </div>
          </div>

          {/* Main message */}
          <div className="max-w-md">
            <h2 className="text-3xl xl:text-4xl font-extrabold text-white leading-tight mb-4">
              Welcome back to your<br/>
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-300 to-purple-300">learning journey</span>
            </h2>
            <p className="text-blue-100/80 text-base font-semibold leading-relaxed mb-8">
              Your child's personalized coding dashboard, progress reports, and upcoming classes — all in one place.
            </p>

            {/* Trust badges */}
            <div className="space-y-3">
              {[
                { icon: Monitor, text: 'Live 1:1 classes with dedicated teachers', color: 'text-cyan-300', bg: 'bg-cyan-500/15' },
                { icon: BookOpen, text: 'Track progress with detailed reports', color: 'text-purple-300', bg: 'bg-purple-500/15' },
                { icon: Shield, text: 'Safe, ad-free learning environment', color: 'text-emerald-300', bg: 'bg-emerald-500/15' },
                { icon: Award, text: 'Trusted by 500+ families worldwide', color: 'text-amber-300', bg: 'bg-amber-500/15' },
              ].map((badge, i) => {
                const Icon = badge.icon;
                return (
                  <div key={i} className="flex items-center gap-3 group">
                    <div className={`w-9 h-9 rounded-lg ${badge.bg} flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-300`}>
                      <Icon className={`w-4.5 h-4.5 ${badge.color}`} />
                    </div>
                    <span className="text-sm font-bold text-blue-100/90">{badge.text}</span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Bottom */}
          <div className="flex items-center gap-4">
            <div className="flex -space-x-2">
              {['A', 'P', 'K', 'D'].map((l, i) => (
                <div key={i} className="w-8 h-8 rounded-full border-2 border-[#1E3A8A] bg-gradient-to-br from-cyan-400 to-purple-400 flex items-center justify-center text-white text-[10px] font-extrabold">{l}</div>
              ))}
            </div>
            <p className="text-xs font-bold text-blue-200/60">Join Ariel, Prithavi, Kimayra, Daksh & more learners</p>
          </div>
        </div>
      </div>

      {/* ═══════ RIGHT PANEL — Login Form ═══════ */}
      <div className="w-full lg:w-[48%] flex items-center justify-center bg-[#F8FAFC] relative px-6 sm:px-12">
        {/* Back to home */}
        <button onClick={() => navigate('/')} className="absolute top-6 left-6 flex items-center gap-1.5 text-xs font-bold text-slate-400 hover:text-slate-700 transition-colors group" data-testid="back-to-home">
          <ArrowLeft className="w-3.5 h-3.5 group-hover:-translate-x-0.5 transition-transform" /> Home
        </button>

        {/* Mobile logo (visible on small screens only) */}
        <div className="lg:hidden absolute top-6 right-6">
          <img src="/assets/logo.png" alt="CodeITT" className="h-10 w-auto object-contain rounded-lg" />
        </div>

        <div className="w-full max-w-[380px]">
          {/* Role indicator pill */}
          <div className={`inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-gradient-to-r ${roleColors[selectedRole]} mb-6`}>
            <Sparkles className="w-3.5 h-3.5 text-white/80" />
            <span className="text-[11px] font-extrabold text-white uppercase tracking-wider">{roleLabels[selectedRole]}</span>
          </div>

          {/* Heading */}
          <h2 className="text-2xl sm:text-3xl font-extrabold text-[#0F172A] mb-1.5 tracking-tight" data-testid="login-heading">Sign in to your account</h2>
          <p className="text-sm font-semibold text-[#64748B] mb-8">Access your dashboard, classes, and progress</p>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-5" data-testid="login-form">
            <div>
              <Label className="text-[#334155] font-extrabold text-xs uppercase tracking-wide mb-2 block">I am a</Label>
              <Select value={selectedRole} onValueChange={setSelectedRole}>
                <SelectTrigger className="bg-white border-slate-200 text-[#0F172A] font-bold h-12 rounded-xl shadow-sm hover:border-slate-300 transition-colors" data-testid="role-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="student">Student</SelectItem>
                  <SelectItem value="teacher">Teacher</SelectItem>
                  <SelectItem value="parent">Parent</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-[#334155] font-extrabold text-xs uppercase tracking-wide mb-2 block">Username or Email</Label>
              <div className="relative">
                <User className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  type="text"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your username or email"
                  className="pl-11 bg-white border-slate-200 text-[#0F172A] font-bold placeholder:text-slate-400 h-12 rounded-xl shadow-sm hover:border-slate-300 focus:border-blue-400 focus:ring-blue-400/20 transition-colors"
                  required
                  data-testid="login-username"
                />
              </div>
            </div>

            <div>
              <Label className="text-[#334155] font-extrabold text-xs uppercase tracking-wide mb-2 block">Password</Label>
              <div className="relative">
                <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  className="pl-11 bg-white border-slate-200 text-[#0F172A] font-bold placeholder:text-slate-400 h-12 rounded-xl shadow-sm hover:border-slate-300 focus:border-blue-400 focus:ring-blue-400/20 transition-colors"
                  required
                  data-testid="login-password"
                />
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className={`w-full bg-gradient-to-r ${roleColors[selectedRole]} hover:opacity-90 text-white h-12 rounded-xl text-sm font-extrabold uppercase tracking-wide shadow-lg transition-all duration-200 hover:shadow-xl hover:scale-[1.01]`}
              data-testid="login-submit"
            >
              {loading ? (
                <span className="flex items-center gap-2"><span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> Signing in...</span>
              ) : (
                <span className="flex items-center gap-2">Sign In <ChevronRight className="w-4 h-4" /></span>
              )}
            </Button>
          </form>

          {/* Divider */}
          <div className="flex items-center gap-3 my-6">
            <div className="flex-1 h-px bg-slate-200" />
            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">New to CodeITT?</span>
            <div className="flex-1 h-px bg-slate-200" />
          </div>

          {/* Secondary CTA */}
          <button onClick={() => navigate('/')}
            className="w-full py-3 rounded-xl border-2 border-slate-200 bg-white text-[#0F172A] font-bold text-sm hover:border-blue-300 hover:bg-blue-50/50 transition-all flex items-center justify-center gap-2 shadow-sm"
            data-testid="book-trial-btn">
            <Sparkles className="w-4 h-4 text-blue-500" />
            Book a Free Trial Class
          </button>

          {/* Mobile trust badges */}
          <div className="lg:hidden mt-8 flex items-center justify-center gap-4 text-[10px] font-bold text-slate-400">
            <span className="flex items-center gap-1"><Shield className="w-3 h-3" /> Safe</span>
            <span className="w-1 h-1 rounded-full bg-slate-300" />
            <span className="flex items-center gap-1"><Users className="w-3 h-3" /> 500+ Families</span>
            <span className="w-1 h-1 rounded-full bg-slate-300" />
            <span className="flex items-center gap-1"><Monitor className="w-3 h-3" /> 1:1 Classes</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
